import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Chip,
  Input,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Select,
  SelectItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Textarea,
} from '@nextui-org/react';
import { Icon } from '@iconify/react/offline';
import PaperclipIcon from '@iconify-icons/tabler/paperclip';
import FileIcon from '@iconify-icons/tabler/file-text';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'common/src/hooks/useTranslation';
import type {
  CaseAction,
  CaseCommandResult,
  CaseOptions,
  CaseOwner,
  CaseScope,
} from 'common/src/api/shipment-cases';
import { SINISTER_RESPONSIBLE_OPTIONS } from 'common/src/utils/sinister';
import { Alert, StatusChip } from '../../components/ui';
import { useCaseCommand } from './use-case-command';
import { OwnerSelect } from './CaseForm';
import CaseReasons from './CaseReasons';

const MAX_FILES = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_FILES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'text/plain': ['.txt'],
  'message/rfc822': ['.eml'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
};
const DUE_DAYS = ['3', '7', '14'];
const ownerBody = (value: string): CaseOwner => ({
  kind: 'user',
  id: Number(value.slice(5)),
});

/** Fila del formulario: título y ayuda a la izquierda, contenido a la derecha. */
export function NewCaseSection({
  title,
  help,
  aside,
  children,
  last,
}: {
  title: string;
  help?: string;
  aside?: ReactNode;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <section
      className={`grid gap-4 py-5 md:grid-cols-[200px_minmax(0,1fr)] md:gap-x-7 ${last ? '' : 'border-b border-default-200'}`}
    >
      <div className='flex flex-col gap-1'>
        <h3 className='text-sm font-semibold'>{title}</h3>
        {help && <p className='text-xs text-default-500'>{help}</p>}
        {aside}
      </div>
      <div className='flex min-w-0 flex-col gap-4'>{children}</div>
    </section>
  );
}

export function NewCaseHeader() {
  const { t } = useTranslation('shipmentCases');
  return (
    <ModalHeader className='flex flex-col gap-0 border-b border-default-200'>
      <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
        {t('Reclamos')}
      </span>
      <span className='text-lg font-semibold'>{t('Nuevo caso')}</span>
    </ModalHeader>
  );
}

export function NewCaseFooter({
  status,
  ready,
  onCancel,
  locked,
  submitLabel,
  submitDisabled,
  busy,
  alerts,
}: {
  status: string;
  ready: boolean;
  onCancel: () => void;
  locked: boolean;
  submitLabel: string;
  submitDisabled: boolean;
  busy?: boolean;
  /** Avisos que deben verse junto al botón, fuera del área que se desplaza. */
  alerts?: ReactNode;
}) {
  const { t } = useTranslation('shipmentCases');
  return (
    <>
      {alerts && (
        <div className='flex shrink-0 flex-col gap-3 border-t border-default-200 px-6 py-3'>
          {alerts}
        </div>
      )}
      <ModalFooter className='flex-wrap items-center gap-3 border-t border-default-200'>
        <Button variant='light' onPress={onCancel} isDisabled={locked}>
          {t('Cancelar')}
        </Button>
        <p
          aria-live='polite'
          className={`order-first flex w-full items-center gap-2 text-xs sm:order-none sm:w-auto sm:flex-1 ${ready ? 'text-success-600' : 'text-default-500'}`}
        >
          <span
            className={`size-1.5 shrink-0 rounded-full ${ready ? 'bg-success' : 'bg-default-300'}`}
          />
          {status}
        </p>
        <Button
          color='primary'
          type='submit'
          isLoading={busy}
          isDisabled={submitDisabled}
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </>
  );
}

export default function NewCaseForm({
  scope,
  identity,
  options,
  shipmentId,
  despacho,
  onSaved,
  onCancel,
  onLockedChange,
}: {
  scope: CaseScope;
  identity: string;
  /** null mientras no hay despacho elegido o su carga está cargando. */
  options: CaseOptions | null;
  shipmentId: number | null;
  /** Sección "Despacho" (buscador y resumen), armada por el modal. */
  despacho: ReactNode;
  onSaved: (r: CaseCommandResult) => void;
  onCancel: () => void;
  onLockedChange: (locked: boolean) => void;
}) {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const [selectedBoxes, setBoxes] = useState<string[]>([]);
  const [selectedReasons, setReasons] = useState<string[]>([]);
  const [reasonsReady, setReasonsReady] = useState(false);
  const [responsible, setResponsible] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileWarning, setFileWarning] = useState<string | null>(null);
  const [caseNumber, setCaseNumber] = useState('');
  const [kickoff, setKickoff] = useState(false);
  const [initialRecipient, setInitialRecipient] = useState('');
  const [initialNote, setInitialNote] = useState('');
  const [initialDays, setInitialDays] = useState('7');
  const command = useCaseCommand(scope, identity, onSaved);
  const frozen = command.frozen || command.busy;
  useEffect(() => {
    onLockedChange(frozen);
    return () => onLockedChange(false);
  }, [frozen, onLockedChange]);

  const cargo = options?.cargo || [];
  const people = options?.people || [];
  const selectable = cargo.filter((b) => b.kilos > 0);
  const totalKilos = selectable.reduce((n, b) => n + Number(b.kilos), 0);
  const kilos = cargo
    .filter((b) => selectedBoxes.includes(String(b.id)))
    .reduce((n, b) => n + Number(b.kilos), 0);
  const percent = totalKilos ? Math.round((kilos / totalKilos) * 100) : 0;
  const selectedOverlap = cargo.some(
    (b) => selectedBoxes.includes(String(b.id)) && b.active_cases.length > 0
  );
  const disabledKeys = frozen
    ? cargo.map((b) => String(b.id))
    : cargo.filter((b) => !(b.kilos > 0)).map((b) => String(b.id));

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (frozen) return;
      setFiles((current) => {
        const next = [...current, ...accepted].slice(0, MAX_FILES);
        const skipped =
          rejected.length + current.length + accepted.length - next.length;
        setFileWarning(
          skipped > 0
            ? t(
                'Se omitieron {{count}} archivos: solo se admiten hasta 10, de máximo 10 MB y de los tipos permitidos.',
                { count: skipped }
              )
            : null
        );
        return next;
      });
    },
    [frozen, t]
  );
  const dropzone = useDropzone({
    onDrop,
    disabled: frozen,
    accept: ACCEPTED_FILES,
    maxSize: MAX_FILE_BYTES,
  });

  const validRecipient = people.some(
    (p) => initialRecipient === `user:${p.id}`
  );
  const missing = [
    !shipmentId && t('el despacho'),
    shipmentId && !selectedBoxes.length && t('la carga afectada'),
    shipmentId && !selectedReasons.length && t('un motivo'),
    kickoff && !validRecipient && t('el destinatario'),
    kickoff && !initialNote.trim() && t('qué necesitas del equipo'),
  ].filter((v): v is string => Boolean(v));
  const valid =
    Boolean(options && shipmentId) &&
    missing.length === 0 &&
    selectedReasons.length <= 10 &&
    reasonsReady &&
    (!kickoff || DUE_DAYS.includes(initialDays));
  const lines = (n: number) =>
    t(n === 1 ? '{{n}} línea' : '{{n}} líneas', { n });
  const status = valid
    ? t('{{kilos}} kg · {{lines}} · {{reasons}}', {
        kilos: kilos.toLocaleString(),
        lines: lines(selectedBoxes.length),
        reasons: t(
          selectedReasons.length === 1 ? '{{n}} motivo' : '{{n}} motivos',
          { n: selectedReasons.length }
        ),
      })
    : missing.length
      ? t('Falta {{items}}', {
          items:
            missing.length > 1
              ? t('{{list}} y {{last}}', {
                  list: missing.slice(0, -1).join(', '),
                  last: missing[missing.length - 1],
                })
              : missing[0],
        })
      : t('Cargando motivos…');
  const dueDate = new Intl.DateTimeFormat(
    currentLanguage === 'en' ? 'en-US' : 'es-CL',
    { day: '2-digit', month: '2-digit', year: 'numeric' }
  ).format(Date.now() + Number(initialDays) * 86400000);

  const body: CaseAction = {
    action: 'create',
    note,
    shipment_id: shipmentId ?? undefined,
    reasons: selectedReasons,
    box_ids: selectedBoxes.map(Number),
    responsible,
    responsible_name: responsibleName,
    case_number: caseNumber.trim() || undefined,
    initial_request: kickoff
      ? {
          recipient_owner: ownerBody(initialRecipient),
          note: initialNote,
          due_days: Number(initialDays),
        }
      : undefined,
  };

  return (
    <form
      className='contents'
      onSubmit={(e) => {
        e.preventDefault();
        if (valid || frozen) void command.save(body, files);
      }}
      onKeyDown={(e) => {
        // Enter en un campo de una línea no crea el caso; solo el botón del pie.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT')
          e.preventDefault();
      }}
    >
      <NewCaseHeader />
      <ModalBody className='gap-0 py-0'>
        {despacho}
        {options && shipmentId && (
          <>
            <NewCaseSection
              title={t('Carga afectada')}
              help={t(
                'Marca los pallets o líneas afectadas. Los kilos se calculan desde las cajas y su peso.'
              )}
              aside={
                <div className='mt-2 flex flex-col gap-1 rounded-medium border border-default-200 p-4'>
                  <span className='text-xs uppercase tracking-wide text-default-500'>
                    {t('Kg afectados')}
                  </span>
                  <span className='font-mono text-2xl font-semibold'>
                    {kilos.toLocaleString()}{' '}
                    <span className='font-sans text-xs font-normal text-default-500'>
                      {t('{{n}} de {{total}}', {
                        n: '',
                        total: totalKilos.toLocaleString(),
                      }).trim()}
                    </span>
                  </span>
                  <Progress
                    aria-label={t('Kg afectados')}
                    size='sm'
                    value={percent}
                  />
                  <span className='text-xs text-default-500'>
                    {t('{{n}} de {{total}}', {
                      n: selectedBoxes.length,
                      total: lines(selectable.length),
                    })}{' '}
                    · {percent}%
                  </span>
                </div>
              }
            >
              <div className='max-h-80 overflow-auto rounded-medium border border-default-200'>
                <Table
                  removeWrapper
                  isCompact
                  isHeaderSticky
                  aria-label={t('Carga afectada')}
                  selectionMode='multiple'
                  color='primary'
                  selectedKeys={new Set(selectedBoxes)}
                  disabledKeys={new Set(disabledKeys)}
                  onSelectionChange={(keys) =>
                    setBoxes(
                      keys === 'all'
                        ? selectable.map((b) => String(b.id))
                        : Array.from(keys).map(String)
                    )
                  }
                >
                  <TableHeader>
                    <TableColumn>{t('Pallet')}</TableColumn>
                    <TableColumn>{t('Variedad')}</TableColumn>
                    <TableColumn>{t('Calibre')}</TableColumn>
                    <TableColumn>{t('Cajas')}</TableColumn>
                    <TableColumn className='text-right'>{t('Kg')}</TableColumn>
                    <TableColumn className='text-right'>
                      {t('Casos activos')}
                    </TableColumn>
                  </TableHeader>
                  <TableBody
                    emptyContent={t('Este despacho no tiene carga con peso.')}
                  >
                    {cargo.map((b) => (
                      <TableRow key={String(b.id)}>
                        <TableCell className='whitespace-nowrap font-mono font-semibold'>
                          {b.pallet || t('Sin pallet')}
                        </TableCell>
                        <TableCell>{b.variety}</TableCell>
                        <TableCell className='text-default-500'>
                          {b.caliber || '—'}
                        </TableCell>
                        <TableCell className='whitespace-nowrap text-default-500'>
                          {b.quantity} × {b.box_weight} kg
                        </TableCell>
                        <TableCell className='text-right font-mono'>
                          {b.kilos > 0
                            ? Number(b.kilos).toLocaleString()
                            : t('Sin peso')}
                        </TableCell>
                        <TableCell className='text-right'>
                          {b.active_cases.length ? (
                            <StatusChip tone='warning'>
                              {b.active_cases.join(', ')}
                            </StatusChip>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {selectedOverlap && (
                <Alert variant='warning'>
                  {t(
                    'Esta carga ya tiene casos activos. El resumen cuenta los kilos una vez y usa la mayor pérdida estimada por línea.'
                  )}
                </Alert>
              )}
            </NewCaseSection>
            <NewCaseSection
              title={t('Motivos')}
              help={t('Puedes marcar más de uno.')}
            >
              <CaseReasons
                exporterId={scope.exporter_id}
                identity={identity}
                selected={selectedReasons}
                onSelectionChange={setReasons}
                onReadyChange={setReasonsReady}
                disabled={frozen}
                showTitle={false}
              />
            </NewCaseSection>
            <NewCaseSection
              title={t('Responsable')}
              help={t(
                'A quién se atribuye la pérdida. Puedes dejarlo sin asignar y editarlo después.'
              )}
            >
              <div className='grid gap-4 sm:grid-cols-2'>
                <Select
                  label={t('Responsable de la pérdida')}
                  selectedKeys={responsible ? [responsible] : ['']}
                  onSelectionChange={(keys) =>
                    setResponsible(String(Array.from(keys)[0] || ''))
                  }
                  isDisabled={frozen}
                >
                  {[
                    { key: '', label: t('Sin asignar') },
                    ...SINISTER_RESPONSIBLE_OPTIONS.map((v) => ({
                      key: v,
                      label: v,
                    })),
                  ].map((o) => (
                    <SelectItem key={o.key} textValue={o.label}>
                      {o.label}
                    </SelectItem>
                  ))}
                </Select>
                <Input
                  label={t('Responsable específico (opcional)')}
                  placeholder={t('Nombre o razón social')}
                  value={responsibleName}
                  onValueChange={setResponsibleName}
                  isDisabled={frozen}
                />
              </div>
            </NewCaseSection>
            <NewCaseSection
              title={t('Descripción')}
              help={t(
                'Qué pasó y evidencia de respaldo: fotos, informes, registros de temperatura.'
              )}
            >
              <Textarea
                label={t('Comentario')}
                placeholder={t('Describe lo ocurrido…')}
                minRows={3}
                value={note}
                onValueChange={setNote}
                maxLength={8000}
                isDisabled={frozen}
              />
              <div
                {...dropzone.getRootProps()}
                aria-label={t('Adjuntar documentos')}
                className={`flex cursor-pointer items-center gap-3 rounded-medium border border-dashed p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${
                  dropzone.isDragActive
                    ? 'border-primary bg-primary-50'
                    : 'border-default-300 hover:border-default-400'
                } ${frozen ? 'pointer-events-none opacity-60' : ''}`}
              >
                <input {...dropzone.getInputProps()} />
                <span className='flex size-8 shrink-0 items-center justify-center rounded-medium bg-default-100 text-default-500'>
                  <Icon icon={PaperclipIcon} width={16} />
                </span>
                <span className='flex min-w-0 flex-1 flex-col'>
                  <span className='text-sm font-medium'>
                    {t('Adjuntar documentos')}
                  </span>
                  <span className='text-xs text-default-500'>
                    {t(
                      'Arrastra aquí o haz clic para elegir · máximo 10 archivos, hasta 10 MB cada uno'
                    )}
                  </span>
                </span>
                <span className='whitespace-nowrap text-xs text-default-500'>
                  {files.length
                    ? t('{{n}} de {{total}}', {
                        n: files.length,
                        total: MAX_FILES,
                      })
                    : t('Sin archivos')}
                </span>
              </div>
              {fileWarning && <Alert variant='warning'>{fileWarning}</Alert>}
              {files.length > 0 && (
                <div className='flex flex-wrap gap-2'>
                  {files.map((f, i) => (
                    <Chip
                      key={`${f.name}:${i}`}
                      size='sm'
                      variant='flat'
                      className='max-w-full'
                      classNames={{ content: 'truncate' }}
                      startContent={<Icon icon={FileIcon} width={12} />}
                      onClose={
                        frozen
                          ? undefined
                          : () => {
                              setFileWarning(null);
                              setFiles((current) =>
                                current.filter((_, j) => j !== i)
                              );
                            }
                      }
                    >
                      {f.name} · {Math.ceil(f.size / 1024).toLocaleString()} KB
                    </Chip>
                  ))}
                </div>
              )}
            </NewCaseSection>
            <NewCaseSection
              title={t('Opciones')}
              help={t('Numeración propia y arranque del flujo.')}
              last
            >
              <div className='grid gap-4 sm:grid-cols-2'>
                <Input
                  label={t('Número de caso (opcional)')}
                  description={t(
                    'Tu propio número o código. Si lo dejas vacío se usa el correlativo CR.'
                  )}
                  placeholder={t('CR automático')}
                  maxLength={40}
                  value={caseNumber}
                  onValueChange={setCaseNumber}
                  isDisabled={frozen}
                  classNames={{ input: 'font-mono' }}
                />
                <Switch
                  size='sm'
                  isSelected={kickoff}
                  onValueChange={setKickoff}
                  isDisabled={frozen}
                  classNames={{
                    // design-ignore: tarjeta seleccionable
                    base: `m-0 inline-flex w-full max-w-full flex-row-reverse items-center justify-between gap-3 rounded-medium border p-3 ${kickoff ? 'border-primary' : 'border-default-200'}`,
                    label: 'ms-0 flex flex-col',
                  }}
                >
                  <span className='text-sm font-medium'>
                    {t('Iniciar el flujo con una solicitud al equipo')}
                  </span>
                  <span className='text-xs text-default-500'>
                    {t(
                      'Pide información a alguien del equipo al crear el caso.'
                    )}
                  </span>
                </Switch>
                {kickoff && (
                  <div className='grid gap-4 rounded-medium border border-default-200 p-4 sm:col-span-2 sm:grid-cols-2'>
                    <p className='text-xs font-semibold uppercase tracking-wide text-default-500 sm:col-span-2'>
                      {t('Solicitud inicial al equipo')}
                    </p>
                    <OwnerSelect
                      value={initialRecipient}
                      onChange={setInitialRecipient}
                      people={people}
                      disabled={frozen}
                      label='Destinatario interno'
                    />
                    <Select
                      label={t('Plazo de respuesta')}
                      description={t('Vence el {{date}}', { date: dueDate })}
                      selectedKeys={[initialDays]}
                      onSelectionChange={(keys) =>
                        setInitialDays(String(Array.from(keys)[0] || ''))
                      }
                      isDisabled={frozen}
                    >
                      {DUE_DAYS.map((d) => (
                        <SelectItem key={d} textValue={`${d} ${t('días')}`}>
                          {d} {t('días')}
                        </SelectItem>
                      ))}
                    </Select>
                    <Textarea
                      className='sm:col-span-2'
                      label={t('Qué necesitas del equipo')}
                      minRows={2}
                      value={initialNote}
                      onValueChange={setInitialNote}
                      isDisabled={frozen}
                      maxLength={8000}
                    />
                  </div>
                )}
              </div>
            </NewCaseSection>
          </>
        )}
      </ModalBody>
      <NewCaseFooter
        status={status}
        ready={valid}
        onCancel={onCancel}
        locked={frozen}
        busy={command.busy}
        submitDisabled={!valid && !frozen}
        submitLabel={t(
          frozen
            ? 'Reintentar envío'
            : kickoff
              ? 'Crear caso y pedir información'
              : 'Crear caso'
        )}
        alerts={
          command.error || (frozen && !command.busy) ? (
            <>
              {command.error && (
                <Alert variant='danger' showIcon>
                  {command.error}
                </Alert>
              )}
              {frozen && !command.busy && (
                <Alert variant='warning'>
                  {t(
                    'El envío está fijado para reintentar sin duplicar la acción. Conserva esta ventana abierta hasta completarlo.'
                  )}
                </Alert>
              )}
            </>
          ) : null
        }
      />
    </form>
  );
}
